import { useState, useMemo } from 'react'
import {
  TrendingUp,
  Users,
  DollarSign,
  Target,
  AlertCircle,
  CheckCircle,
  BarChart3,
  PieChart,
  Calendar,
  Zap,
  Plus,
  Edit2,
  Trash2,
  X,
  Save,
  Brain,
  Lightbulb,
  TrendingDown,
  Clock,
  Loader,
} from 'lucide-react'
import { useCompanyGrowthStore } from '../store/companyGrowthStore'
import { useAIMonitoring } from '../hooks/useAIMonitoring'

export default function CompanyGrowthMonitorPage() {
  const store = useCompanyGrowthStore()
  const company = store.company
  const growthMetrics = store.getGrowthMetrics()
  const products = store.products
  const monthlyData = store.monthlyData
  const opportunities = store.opportunities
  const risks = store.risks

  // AI Monitoring
  const { insights, recommendations, alerts, isAnalyzing, lastAnalysis, analyzeCompanyData } = useAIMonitoring(true, 300000)

  // Modal states
  const [showProductModal, setShowProductModal] = useState(false)
  const [showMonthModal, setShowMonthModal] = useState(false)
  const [showOppModal, setShowOppModal] = useState(false)
  const [showRiskModal, setShowRiskModal] = useState(false)
  const [showAIPanel, setShowAIPanel] = useState(false)
  const [editingId, setEditingId] = useState(null)

  // Form states
  const [productForm, setProductForm] = useState({ name: '', revenue: '', customers: '', growth: '' })
  const [monthForm, setMonthForm] = useState({ month: '', revenue: '', customers: '', churn: '' })
  const [oppForm, setOppForm] = useState({ title: '', potential: '', timeline: '', resources: '', likelihood: 'Medium', status: 'Planning' })
  const [riskForm, setRiskForm] = useState({ risk: '', impact: 'Medium', mitigation: '', status: 'Monitoring' })

  // Product handlers
  const handleAddProduct = () => {
    if (productForm.name && productForm.revenue) {
      if (editingId) {
        store.updateProduct(editingId, {
          name: productForm.name,
          revenue: parseInt(productForm.revenue),
          customers: parseInt(productForm.customers),
          growth: parseInt(productForm.growth),
        })
        setEditingId(null)
      } else {
        store.addProduct({
          name: productForm.name,
          revenue: parseInt(productForm.revenue),
          customers: parseInt(productForm.customers),
          growth: parseInt(productForm.growth),
        })
      }
      setProductForm({ name: '', revenue: '', customers: '', growth: '' })
      setShowProductModal(false)
    }
  }

  const handleEditProduct = (product) => {
    setProductForm(product)
    setEditingId(product.id)
    setShowProductModal(true)
  }

  const handleDeleteProduct = (id) => {
    if (window.confirm('Delete this product?')) {
      store.deleteProduct(id)
    }
  }

  // Month handlers
  const handleAddMonth = () => {
    if (monthForm.month && monthForm.revenue) {
      if (editingId) {
        store.updateMonthData(editingId, {
          month: monthForm.month,
          revenue: parseInt(monthForm.revenue),
          customers: parseInt(monthForm.customers),
          churn: parseInt(monthForm.churn),
        })
        setEditingId(null)
      } else {
        store.addMonthData({
          month: monthForm.month,
          revenue: parseInt(monthForm.revenue),
          customers: parseInt(monthForm.customers),
          churn: parseInt(monthForm.churn),
        })
      }
      setMonthForm({ month: '', revenue: '', customers: '', churn: '' })
      setShowMonthModal(false)
    }
  }

  const handleEditMonth = (data) => {
    setMonthForm(data)
    setEditingId(data.id)
    setShowMonthModal(true)
  }

  const handleDeleteMonth = (id) => {
    if (window.confirm('Delete this month data?')) {
      store.deleteMonthData(id)
    }
  }

  // Opportunity handlers
  const handleAddOpportunity = () => {
    if (oppForm.title && oppForm.potential) {
      if (editingId) {
        store.updateOpportunity(editingId, oppForm)
        setEditingId(null)
      } else {
        store.addOpportunity(oppForm)
      }
      setOppForm({ title: '', potential: '', timeline: '', resources: '', likelihood: 'Medium', status: 'Planning' })
      setShowOppModal(false)
    }
  }

  const handleEditOpportunity = (opp) => {
    setOppForm(opp)
    setEditingId(opp.id)
    setShowOppModal(true)
  }

  const handleDeleteOpportunity = (id) => {
    if (window.confirm('Delete this opportunity?')) {
      store.deleteOpportunity(id)
    }
  }

  // Risk handlers
  const handleAddRisk = () => {
    if (riskForm.risk && riskForm.mitigation) {
      if (editingId) {
        store.updateRisk(editingId, riskForm)
        setEditingId(null)
      } else {
        store.addRisk(riskForm)
      }
      setRiskForm({ risk: '', impact: 'Medium', mitigation: '', status: 'Monitoring' })
      setShowRiskModal(false)
    }
  }

  const handleEditRisk = (risk) => {
    setRiskForm(risk)
    setEditingId(risk.id)
    setShowRiskModal(true)
  }

  const handleDeleteRisk = (id) => {
    if (window.confirm('Delete this risk?')) {
      store.deleteRisk(id)
    }
  }

  const closeModals = () => {
    setShowProductModal(false)
    setShowMonthModal(false)
    setShowOppModal(false)
    setShowRiskModal(false)
    setEditingId(null)
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Company Growth Monitor</h1>
          <p className="text-blue-200 mt-1">AI-driven growth metrics and company analytics</p>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={analyzeCompanyData}
            disabled={isAnalyzing}
            className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-cyan-500/50 transition-all disabled:opacity-50"
          >
            {isAnalyzing ? <Loader size={18} className="animate-spin" /> : <Brain size={18} />}
            <span>{isAnalyzing ? 'Analyzing...' : 'Analyze Now'}</span>
          </button>
          <div className="text-right">
            <p className="text-sm text-blue-200">{company.name}</p>
            <p className="text-xs text-blue-300/70">{company.stage} • Founded {company.founded}</p>
          </div>
        </div>
      </div>

      {/* AI Alerts Section */}
      {alerts.length > 0 && (
        <div className="space-y-3">
          {alerts.map((alert, idx) => (
            <div
              key={idx}
              className={`rounded-lg p-4 border ${
                alert.severity === 'critical'
                  ? 'bg-red-500/10 border-red-500/30'
                  : 'bg-orange-500/10 border-orange-500/30'
              }`}
            >
              <div className="flex items-start gap-3">
                <AlertCircle
                  size={20}
                  className={alert.severity === 'critical' ? 'text-red-400' : 'text-orange-400'}
                />
                <div className="flex-1">
                  <p className={`font-bold ${alert.severity === 'critical' ? 'text-red-300' : 'text-orange-300'}`}>
                    {alert.message}
                  </p>
                  <p className="text-xs text-slate-300 mt-1">{alert.details}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* AI Insights Panel */}
      {insights.length > 0 && (
        <div className="bg-gradient-to-r from-slate-800/40 to-slate-900/40 rounded-lg border border-cyan-500/20 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Brain size={24} className="text-cyan-400" />
              <h2 className="text-xl font-bold text-white">AI Insights & Analysis</h2>
            </div>
            <button
              onClick={() => setShowAIPanel(!showAIPanel)}
              className="text-cyan-400 hover:text-cyan-300 text-sm font-semibold"
            >
              {showAIPanel ? 'Hide' : 'Show'} All
            </button>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {(showAIPanel ? insights : insights.slice(0, 2)).map((insight, idx) => (
              <div
                key={idx}
                className={`p-4 rounded-lg border ${
                  insight.type === 'positive'
                    ? 'bg-green-500/10 border-green-500/20'
                    : insight.type === 'warning'
                    ? 'bg-yellow-500/10 border-yellow-500/20'
                    : 'bg-blue-500/10 border-blue-500/20'
                }`}
              >
                <p className="text-sm text-white">{insight.text}</p>
                {insight.priority && (
                  <div className="mt-2">
                    <span
                      className={`text-xs px-2 py-1 rounded-full ${
                        insight.priority === 'critical'
                          ? 'bg-red-500/20 text-red-300'
                          : insight.priority === 'high'
                          ? 'bg-orange-500/20 text-orange-300'
                          : insight.priority === 'medium'
                          ? 'bg-yellow-500/20 text-yellow-300'
                          : 'bg-blue-500/20 text-blue-300'
                      }`}
                    >
                      {insight.priority.toUpperCase()} PRIORITY
                    </span>
                  </div>
                )}
              </div>
            ))}
          </div>
          {lastAnalysis && (
            <p className="text-xs text-slate-400 mt-4">
              Last analyzed: {lastAnalysis.toLocaleTimeString()}
            </p>
          )}
        </div>
      )}

      {/* Key Growth Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Annual Revenue</p>
            <DollarSign className="text-green-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">${(growthMetrics.arr / 1000).toFixed(0)}K</p>
          <p className="text-xs text-green-600 mt-2">↑ {growthMetrics.revenueGrowth}% vs last month</p>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Total Customers</p>
            <Users className="text-blue-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">{store.metrics.customers.total}</p>
          <p className="text-xs text-blue-600 mt-2">↑ {growthMetrics.customerGrowth}% YTD growth</p>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">Avg Revenue/Customer</p>
            <BarChart3 className="text-purple-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">${growthMetrics.acvMrr}</p>
          <p className="text-xs text-purple-600 mt-2">Monthly recurring</p>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm text-blue-200 font-medium">LTV:CAC Ratio</p>
            <TrendingUp className="text-orange-600" size={20} />
          </div>
          <p className="text-3xl font-bold text-white">{growthMetrics.ltv_cac}x</p>
          <p className="text-xs text-orange-600 mt-2">Healthy: &gt; 3x target</p>
        </div>
      </div>

      {/* Revenue & Customer Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Monthly Revenue Trend */}
        <div className="lg:col-span-2 bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-white">Revenue Growth Trajectory</h2>
            <button
              onClick={() => {
                setMonthForm({ month: '', revenue: '', customers: '', churn: '' })
                setEditingId(null)
                setShowMonthModal(true)
              }}
              className="btn-primary flex items-center space-x-1 text-xs cursor-pointer">
              <Plus size={16} />
              <span>Add Month</span>
            </button>
          </div>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {monthlyData.map((period) => {
              const maxRevenue = Math.max(...monthlyData.map(p => p.revenue))
              const percentage = (period.revenue / maxRevenue) * 100
              return (
                <div key={period.id}>
                  <div className="flex justify-between items-center mb-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-white">{period.month}</span>
                      <button
                        onClick={() => handleEditMonth(period)}
                        className="text-blue-400 hover:text-blue-200 cursor-pointer">
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteMonth(period.id)}
                        className="text-red-400 hover:text-red-200 cursor-pointer">
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <span className="text-sm font-bold text-white">${period.revenue.toLocaleString()}</span>
                  </div>
                  <div className="w-full bg-slate-700/30 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: `${percentage}%` }}></div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Unit Economics */}
        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <h2 className="text-xl font-bold text-white mb-4">Unit Economics</h2>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-blue-200 mb-1">Customer Acquisition Cost</p>
              <p className="text-2xl font-bold text-white">${store.metrics.customers.cac}</p>
            </div>
            <div>
              <p className="text-xs text-blue-200 mb-1">Lifetime Value</p>
              <p className="text-2xl font-bold text-white">${store.metrics.customers.ltv.toLocaleString()}</p>
            </div>
            <div className="bg-green-500/20 rounded p-3 border border-green-500/30">
              <p className="text-xs text-green-200 font-bold">LTV/CAC: {growthMetrics.ltv_cac}x</p>
              <p className="text-xs text-green-300 mt-1">✓ Excellent unit economics</p>
            </div>
          </div>
        </div>
      </div>

      {/* Product Revenue Breakdown */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">Revenue by Product</h2>
          <button
            onClick={() => {
              setProductForm({ name: '', revenue: '', customers: '', growth: '' })
              setEditingId(null)
              setShowProductModal(true)
            }}
            className="btn-primary flex items-center space-x-1 text-xs cursor-pointer">
            <Plus size={16} />
            <span>Add Product</span>
          </button>
        </div>
        <div className="space-y-4">
          {products.map((product) => {
            const totalRevenue = products.reduce((sum, p) => sum + p.revenue, 0)
            const percentage = (product.revenue / totalRevenue * 100).toFixed(1)
            return (
              <div key={product.id}>
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <p className="font-medium text-white">{product.name}</p>
                      <button
                        onClick={() => handleEditProduct(product)}
                        className="text-blue-400 hover:text-blue-200 cursor-pointer">
                        <Edit2 size={14} />
                      </button>
                      <button
                        onClick={() => handleDeleteProduct(product.id)}
                        className="text-red-400 hover:text-red-200 cursor-pointer">
                        <Trash2 size={14} />
                      </button>
                    </div>
                    <p className="text-xs text-blue-300/70">{product.customers} customers</p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-white">${product.revenue.toLocaleString()}</p>
                    <p className="text-xs text-green-600">↑ {product.growth}% growth</p>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div className="bg-blue-500 h-2 rounded-full" style={{ width: `${percentage}%` }}></div>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* Team & Organization */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <h2 className="text-xl font-bold text-white mb-4">Team Composition</h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-blue-200">Total Employees</span>
              <span className="text-2xl font-bold text-white">{store.metrics.team.total}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Engineering', value: store.metrics.team.engineering },
                { label: 'Sales', value: store.metrics.team.sales },
                { label: 'Marketing', value: store.metrics.team.marketing },
                { label: 'Operations', value: store.metrics.team.operations },
                { label: 'Support', value: store.metrics.team.support },
              ].map((dept, idx) => (
                <div key={idx} className="bg-slate-700/30 rounded p-2">
                  <p className="text-xs text-blue-200">{dept.label}</p>
                  <p className="text-lg font-bold text-white">{dept.value}</p>
                </div>
              ))}
            </div>
            <div className="pt-3 border-t border-blue-500/30">
              <p className="text-sm text-blue-200">
                Hiring Plan: <span className="font-bold text-blue-600">+{store.metrics.team.hiring_plan} positions</span>
              </p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
          <h2 className="text-xl font-bold text-white mb-4">Funding & Runway</h2>
          <div className="space-y-4">
            <div>
              <p className="text-sm text-blue-200 mb-1">Total Raised</p>
              <p className="text-2xl font-bold text-white">${(store.metrics.funding.raised / 1000).toFixed(0)}K</p>
            </div>
            <div>
              <p className="text-sm text-blue-200 mb-1">Monthly Burn Rate</p>
              <p className="text-2xl font-bold text-white">${(store.metrics.funding.burn_rate / 1000).toFixed(0)}K</p>
            </div>
            <div className="bg-blue-500/20 rounded p-3 border border-blue-500/30">
              <p className="text-sm font-bold text-blue-200">Runway: {store.metrics.funding.runway_months} months</p>
              <p className="text-xs text-blue-300 mt-1">Target: {store.metrics.funding.next_round}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Growth Opportunities */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">🎯 Growth Opportunities</h2>
          <button
            onClick={() => {
              setOppForm({ title: '', potential: '', timeline: '', resources: '', likelihood: 'Medium', status: 'Planning' })
              setEditingId(null)
              setShowOppModal(true)
            }}
            className="btn-primary flex items-center space-x-1 text-xs cursor-pointer">
            <Plus size={16} />
            <span>Add Opportunity</span>
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {opportunities.map((opp) => (
            <div key={opp.id} className="bg-gradient-to-br from-slate-800/50 to-slate-800/50 rounded-lg p-4 border border-blue-500/30">
              <div className="flex items-start justify-between mb-2">
                <h3 className="font-bold text-white flex-1">{opp.title}</h3>
                <div className="flex space-x-2 ml-2">
                  <button
                    onClick={() => handleEditOpportunity(opp)}
                    className="text-blue-400 hover:text-blue-200 cursor-pointer">
                    <Edit2 size={14} />
                  </button>
                  <button
                    onClick={() => handleDeleteOpportunity(opp.id)}
                    className="text-red-400 hover:text-red-200 cursor-pointer">
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-blue-200">Potential ARR:</p>
                  <p className="font-bold text-green-600">{opp.potential}</p>
                </div>
                <div>
                  <p className="text-blue-200">Timeline:</p>
                  <p className="font-bold text-white">{opp.timeline}</p>
                </div>
                <div>
                  <p className="text-blue-200">Status:</p>
                  <p className="font-bold text-white">{opp.status}</p>
                </div>
                <div className="pt-2">
                  <span
                    className={`px-2 py-1 rounded text-xs font-bold ${
                      opp.likelihood === 'High'
                        ? 'bg-green-500/20 text-green-200'
                        : opp.likelihood === 'Medium-High'
                          ? 'bg-blue-500/20 text-blue-200'
                          : 'bg-yellow-500/20 text-yellow-200'
                    }`}
                  >
                    {opp.likelihood} Likelihood
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Risk Assessment */}
      <div className="bg-slate-800/50 rounded-lg border border-blue-500/30 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">⚠️ Risk Assessment</h2>
          <button
            onClick={() => {
              setRiskForm({ risk: '', impact: 'Medium', mitigation: '', status: 'Monitoring' })
              setEditingId(null)
              setShowRiskModal(true)
            }}
            className="btn-primary flex items-center space-x-1 text-xs cursor-pointer">
            <Plus size={16} />
            <span>Add Risk</span>
          </button>
        </div>
        <div className="space-y-3">
          {risks.map((risk) => (
            <div key={risk.id} className="flex items-start space-x-3 pb-3 border-b border-blue-500/20 last:border-b-0">
              <AlertCircle
                className={`flex-shrink-0 mt-0.5 ${
                  risk.impact === 'High' ? 'text-red-600' : 'text-yellow-600'
                }`}
                size={18}
              />
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <p className="font-medium text-white">{risk.risk}</p>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEditRisk(risk)}
                      className="text-blue-400 hover:text-blue-200 cursor-pointer">
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteRisk(risk.id)}
                      className="text-red-400 hover:text-red-200 cursor-pointer">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="flex items-center space-x-3 mt-1">
                  <span className={`text-xs font-bold px-2 py-1 rounded ${
                    risk.impact === 'High'
                      ? 'bg-red-500/20 text-red-200'
                      : 'bg-yellow-500/20 text-yellow-200'
                  }`}>
                    {risk.impact} Impact
                  </span>
                  <span className="text-sm text-blue-200">
                    <strong>Mitigation:</strong> {risk.mitigation}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* AI Recommendations */}
      {recommendations.length > 0 && (
        <div className="bg-gradient-to-r from-cyan-600/10 to-blue-600/10 border border-cyan-500/30 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Lightbulb size={24} className="text-cyan-400" />
            <h2 className="text-xl font-bold text-white">Strategic Recommendations</h2>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {recommendations.map((rec, idx) => (
              <div
                key={idx}
                className="bg-slate-800/60 rounded-lg p-4 border border-cyan-500/20 hover:border-cyan-500/40 transition-all"
              >
                <div className="flex items-start gap-3">
                  <div className="mt-1">
                    <div className={`w-2 h-2 rounded-full ${
                      rec.impact === 'high'
                        ? 'bg-red-400'
                        : rec.impact === 'medium'
                        ? 'bg-yellow-400'
                        : 'bg-blue-400'
                    }`}></div>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-bold text-white text-sm">{rec.action}</h3>
                    <p className="text-xs text-slate-300 mt-2">{rec.rationale}</p>
                    <div className="flex items-center gap-2 mt-3">
                      <span className="text-xs px-2 py-1 bg-slate-700/50 rounded text-slate-300">
                        {rec.timeline}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        rec.impact === 'high'
                          ? 'bg-red-500/20 text-red-300'
                          : rec.impact === 'medium'
                          ? 'bg-yellow-500/20 text-yellow-300'
                          : 'bg-blue-500/20 text-blue-300'
                      }`}>
                        {rec.impact.toUpperCase()} IMPACT
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* AI Growth Insights */}
      <div className="bg-gradient-to-r from-slate-800/50 to-slate-800/50 border border-blue-500/30 rounded-lg p-6">
        <h2 className="text-xl font-bold text-white mb-4">🤖 AI Growth Analysis</h2>
        <div className="space-y-3">
          {store.aiInsights.map((insight, idx) => (
            <div key={idx} className="bg-slate-800/50 rounded p-3 border-l-4 border-blue-500">
              <p className="text-sm text-white">{insight}</p>
            </div>
          ))}
        </div>
      </div>

      {/* MODALS */}

      {/* Product Modal */}
      {showProductModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">{editingId ? 'Edit' : 'Add'} Product</h2>
              <button onClick={closeModals} className="text-blue-300 hover:text-blue-200">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Product Name"
                value={productForm.name}
                onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                className="input-base"
              />
              <input
                type="number"
                placeholder="Revenue"
                value={productForm.revenue}
                onChange={(e) => setProductForm({ ...productForm, revenue: e.target.value })}
                className="input-base"
              />
              <input
                type="number"
                placeholder="Customers"
                value={productForm.customers}
                onChange={(e) => setProductForm({ ...productForm, customers: e.target.value })}
                className="input-base"
              />
              <input
                type="number"
                placeholder="Growth %"
                value={productForm.growth}
                onChange={(e) => setProductForm({ ...productForm, growth: e.target.value })}
                className="input-base"
              />
            </div>
            <div className="flex space-x-3 mt-6">
              <button onClick={handleAddProduct} className="btn-primary flex-1 flex items-center justify-center cursor-pointer">
                <Save size={16} className="mr-2" />
                Save
              </button>
              <button onClick={closeModals} className="btn-secondary flex-1 cursor-pointer">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Month Modal */}
      {showMonthModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">{editingId ? 'Edit' : 'Add'} Month Data</h2>
              <button onClick={closeModals} className="text-blue-300 hover:text-blue-200">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Month (e.g., Jul)"
                value={monthForm.month}
                onChange={(e) => setMonthForm({ ...monthForm, month: e.target.value })}
                className="input-base"
              />
              <input
                type="number"
                placeholder="Revenue"
                value={monthForm.revenue}
                onChange={(e) => setMonthForm({ ...monthForm, revenue: e.target.value })}
                className="input-base"
              />
              <input
                type="number"
                placeholder="Customers"
                value={monthForm.customers}
                onChange={(e) => setMonthForm({ ...monthForm, customers: e.target.value })}
                className="input-base"
              />
              <input
                type="number"
                placeholder="Churn"
                value={monthForm.churn}
                onChange={(e) => setMonthForm({ ...monthForm, churn: e.target.value })}
                className="input-base"
              />
            </div>
            <div className="flex space-x-3 mt-6">
              <button onClick={handleAddMonth} className="btn-primary flex-1 flex items-center justify-center cursor-pointer">
                <Save size={16} className="mr-2" />
                Save
              </button>
              <button onClick={closeModals} className="btn-secondary flex-1 cursor-pointer">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Opportunity Modal */}
      {showOppModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">{editingId ? 'Edit' : 'Add'} Opportunity</h2>
              <button onClick={closeModals} className="text-blue-300 hover:text-blue-200">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Opportunity Title"
                value={oppForm.title}
                onChange={(e) => setOppForm({ ...oppForm, title: e.target.value })}
                className="input-base"
              />
              <input
                type="text"
                placeholder="Potential (e.g., $2.5M ARR)"
                value={oppForm.potential}
                onChange={(e) => setOppForm({ ...oppForm, potential: e.target.value })}
                className="input-base"
              />
              <input
                type="text"
                placeholder="Timeline"
                value={oppForm.timeline}
                onChange={(e) => setOppForm({ ...oppForm, timeline: e.target.value })}
                className="input-base"
              />
              <input
                type="text"
                placeholder="Resources"
                value={oppForm.resources}
                onChange={(e) => setOppForm({ ...oppForm, resources: e.target.value })}
                className="input-base"
              />
              <select
                value={oppForm.likelihood}
                onChange={(e) => setOppForm({ ...oppForm, likelihood: e.target.value })}
                className="input-base">
                <option>High</option>
                <option>Medium-High</option>
                <option>Medium</option>
                <option>Low</option>
              </select>
              <select
                value={oppForm.status}
                onChange={(e) => setOppForm({ ...oppForm, status: e.target.value })}
                className="input-base">
                <option>Planning</option>
                <option>In Progress</option>
                <option>Research</option>
                <option>Completed</option>
              </select>
            </div>
            <div className="flex space-x-3 mt-6">
              <button onClick={handleAddOpportunity} className="btn-primary flex-1 flex items-center justify-center cursor-pointer">
                <Save size={16} className="mr-2" />
                Save
              </button>
              <button onClick={closeModals} className="btn-secondary flex-1 cursor-pointer">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Risk Modal */}
      {showRiskModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-slate-800 rounded-lg border border-blue-500/20 max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-white">{editingId ? 'Edit' : 'Add'} Risk</h2>
              <button onClick={closeModals} className="text-blue-300 hover:text-blue-200">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Risk Description"
                value={riskForm.risk}
                onChange={(e) => setRiskForm({ ...riskForm, risk: e.target.value })}
                className="input-base"
              />
              <input
                type="text"
                placeholder="Mitigation Strategy"
                value={riskForm.mitigation}
                onChange={(e) => setRiskForm({ ...riskForm, mitigation: e.target.value })}
                className="input-base"
              />
              <select
                value={riskForm.impact}
                onChange={(e) => setRiskForm({ ...riskForm, impact: e.target.value })}
                className="input-base">
                <option>High</option>
                <option>Medium</option>
                <option>Low</option>
              </select>
              <select
                value={riskForm.status}
                onChange={(e) => setRiskForm({ ...riskForm, status: e.target.value })}
                className="input-base">
                <option>Monitoring</option>
                <option>Active</option>
                <option>Resolved</option>
              </select>
            </div>
            <div className="flex space-x-3 mt-6">
              <button onClick={handleAddRisk} className="btn-primary flex-1 flex items-center justify-center cursor-pointer">
                <Save size={16} className="mr-2" />
                Save
              </button>
              <button onClick={closeModals} className="btn-secondary flex-1 cursor-pointer">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
